import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, X, Upload, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { CATEGORIES } from "@/lib/api";

const empty = {
    name: "", slug: "", category: CATEGORIES[0].slug,
    summary: "", description: "",
    features: [], specs: [], images: [],
    featured: false,
};

const AdminProductForm = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const nav = useNavigate();
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/products/${id}`).then(({ data }) => {
            setForm({
                name: data.name || "",
                slug: data.slug || "",
                category: data.category || CATEGORIES[0].slug,
                summary: data.summary || "",
                description: data.description || "",
                features: data.features || [],
                specs: data.specs || [],
                images: data.images || [],
                featured: !!data.featured,
            });
        }).catch(() => toast.error("Failed to load product"));
    }, [id, isEdit]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addFeature = () => set("features", [...form.features, ""]);
    const updateFeature = (i, v) => {
        const arr = [...form.features]; arr[i] = v; set("features", arr);
    };
    const removeFeature = (i) => set("features", form.features.filter((_, idx) => idx !== i));

    const addSpec = () => set("specs", [...form.specs, { label: "", value: "" }]);
    const updateSpec = (i, k, v) => {
        const arr = [...form.specs]; arr[i] = { ...arr[i], [k]: v }; set("specs", arr);
    };
    const removeSpec = (i) => set("specs", form.specs.filter((_, idx) => idx !== i));

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const urls = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(data.url);
            }
            set("images", [...form.images, ...urls]);
            toast.success(`${urls.length} image(s) uploaded`);
        } catch (err) {
            toast.error("Upload failed: " + (err.response?.data?.detail || "Try smaller files"));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeImage = (i) => set("images", form.images.filter((_, idx) => idx !== i));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.summary || !form.description) {
            toast.error("Name, summary and description are required.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                features: form.features.filter((f) => f.trim()),
                specs: form.specs.filter((s) => s.label.trim() && s.value.trim()),
            };
            if (isEdit) {
                await api.put(`/products/${id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/products", payload);
                toast.success("Product created");
            }
            nav("/admin/products");
        } catch (err) {
            toast.error("Save failed: " + (err.response?.data?.detail || ""));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-product-form">
            <button onClick={() => nav("/admin/products")} className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500 hover:text-gray-900 inline-flex items-center gap-2 mb-6" data-testid="back-to-products">
                <ArrowLeft size={14} /> Back to Products
            </button>
            <div className="overline text-red-600 mb-3">{isEdit ? "Edit Product" : "New Product"}</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-10">
                {isEdit ? form.name || "Product" : "Add Product"}
            </h1>

            <form onSubmit={submit} className="space-y-10 max-w-4xl">
                <section className="bg-white border border-gray-200 p-8 space-y-5">
                    <div className="overline text-red-600 mb-2">Basics</div>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <label className="label-mono">Name *</label>
                            <input className="input-field" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="form-name" />
                        </div>
                        <div>
                            <label className="label-mono">Slug</label>
                            <input className="input-field" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated" data-testid="form-slug" />
                        </div>
                        <div>
                            <label className="label-mono">Category *</label>
                            <select className="input-field" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="form-category">
                                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="form-featured" />
                                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-700">Show on Homepage</span>
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="label-mono">Summary * (1–2 lines)</label>
                        <input className="input-field" value={form.summary} onChange={(e) => set("summary", e.target.value)} data-testid="form-summary" />
                    </div>
                    <div>
                        <label className="label-mono">Description *</label>
                        <textarea rows={5} className="input-field" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="form-description" />
                    </div>
                </section>

                <section className="bg-white border border-gray-200 p-8">
                    <div className="overline text-red-600 mb-4">Images</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {form.images.map((src, i) => (
                            <div key={i} className="relative aspect-square border border-gray-200 overflow-hidden bg-gray-50" data-testid={`form-image-${i}`}>
                                <img src={src} alt="" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white border border-gray-300 p-1 hover:bg-red-50" data-testid={`remove-image-${i}`}>
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                        <label className="aspect-square border border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-gray-900 text-gray-500 hover:text-gray-900" data-testid="form-upload-label">
                            <Upload size={20} />
                            <span className="font-mono text-[10px] uppercase tracking-[0.18em]">{uploading ? "Uploading…" : "Upload"}</span>
                            <input type="file" multiple accept="image/*" className="hidden" onChange={onUpload} data-testid="form-upload-input" />
                        </label>
                    </div>
                    <p className="mt-3 text-xs text-gray-500">Max 5 MB per file. First image is the cover.</p>
                </section>

                <section className="bg-white border border-gray-200 p-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="overline text-red-600">Key Features</div>
                        <button type="button" onClick={addFeature} className="btn-outline" data-testid="add-feature"><Plus size={14} /> Add</button>
                    </div>
                    <div className="space-y-3">
                        {form.features.map((f, i) => (
                            <div key={i} className="flex gap-2" data-testid={`feature-row-${i}`}>
                                <input className="input-field flex-1" value={f} onChange={(e) => updateFeature(i, e.target.value)} placeholder="Feature highlight" />
                                <button type="button" onClick={() => removeFeature(i)} className="p-3 border border-gray-200 hover:bg-gray-100"><X size={16} /></button>
                            </div>
                        ))}
                        {form.features.length === 0 && <div className="text-sm text-gray-400">No features yet.</div>}
                    </div>
                </section>

                <section className="bg-white border border-gray-200 p-8">
                    <div className="flex items-center justify-between mb-4">
                        <div className="overline text-red-600">Specifications</div>
                        <button type="button" onClick={addSpec} className="btn-outline" data-testid="add-spec"><Plus size={14} /> Add</button>
                    </div>
                    <div className="space-y-3">
                        {form.specs.map((s, i) => (
                            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`spec-row-${i}`}>
                                <input className="input-field" value={s.label} onChange={(e) => updateSpec(i, "label", e.target.value)} placeholder="Label e.g. Power" />
                                <input className="input-field" value={s.value} onChange={(e) => updateSpec(i, "value", e.target.value)} placeholder="Value e.g. 3 kW" />
                                <button type="button" onClick={() => removeSpec(i)} className="p-3 border border-gray-200 hover:bg-gray-100"><X size={16} /></button>
                            </div>
                        ))}
                        {form.specs.length === 0 && <div className="text-sm text-gray-400">No specs yet.</div>}
                    </div>
                </section>

                <div className="flex gap-3 sticky bottom-0 bg-gray-50/80 backdrop-blur py-4 -mx-4 px-4 border-t border-gray-200">
                    <button type="submit" disabled={busy} className="btn-primary" data-testid="form-save">
                        {busy ? "Saving…" : (isEdit ? "Save Changes" : "Create Product")}
                    </button>
                    <button type="button" onClick={() => nav("/admin/products")} className="btn-outline">Cancel</button>
                </div>
            </form>
        </div>
    );
};

export default AdminProductForm;
